import { useEffect, useRef, useState } from "react";
import { Typography, palette, Card, ControlledCheckbox, useHookForm, Form } from "@powerdiary/components-library/lib";
import { alpha, Box, useTheme } from "@mui/material";
import { amountToCurrency } from "utils/localeHelper";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { useClientCreditCardDetails } from "../useClientCreditCardDetails";
import LoadingOverlay from "../../LoadingOverlay";
import StripeDetailsView from "../StripeDetailsView";
import StripePaymentForm from "../StripePaymentForm";
import RecaptchaNotice from "../../RecaptchaNotice";
import {
    generatePaymentFormDefaultValues,
    paymentFormSchema,
    PaymentFormType,
} from "./InvoiceStripePaymentShared.helpers";
import { useStripePayment } from "../useStripePayment";
import { StripePaymentSource } from "types/generated/models";

export interface IStripePaymentProps {
    practitionerId: number;
    invoiceIds: number[];
    amount: number;
    totalAmount?: number;
    clientId?: number;
    fee?: number;
    formId: string;
    onSuccessfulPayment: () => void;
}

export default function InvoiceStripePaymentShared({
    practitionerId,
    invoiceIds,
    amount,
    totalAmount,
    fee,
    clientId,
    formId,
    onSuccessfulPayment,
}: Readonly<IStripePaymentProps>) {
    const [editCardMode, setEditCardMode] = useState(false);

    const { palette: muiPalette } = useTheme();

    const { isLoading: creditCardInfoLoading, creditCardInfo } = useClientCreditCardDetails(practitionerId);
    const { clientPortalConfig } = useClientPortalConfigContext();

    // Webhook persistence is in TaskService; useStripePayment awaits it before calling here.
    const handleSuccessfulPayment = (): Promise<void> => {
        onSuccessfulPayment();
        return Promise.resolve();
    };

    const { handlePayment, savingPayment } = useStripePayment({
        practitionerId,
        invoiceIds,
        editCardMode,
        creditCardInfo,
        handleSuccessfulPayment,
        clientId: clientId,
        stripePaymentSource: StripePaymentSource.ClientPortalInvoicePayment,
    });

    useEffect(() => {
        if (!creditCardInfoLoading) {
            setEditCardMode(!creditCardInfo);
        }
    }, [creditCardInfo, creditCardInfoLoading]);

    const form = useHookForm({
        defaultValues: generatePaymentFormDefaultValues(clientPortalConfig.userLoggedIn),
        schema: paymentFormSchema,
    });
    const { formState, getValues, setValue } = form;
    const previousLoggedInRef = useRef(clientPortalConfig.userLoggedIn);

    useEffect(() => {
        const wasLoggedIn = previousLoggedInRef.current;
        const isLoggedIn = clientPortalConfig.userLoggedIn;
        const storeCardDirty = !!formState.dirtyFields?.storeCard;

        if (!wasLoggedIn && isLoggedIn && !storeCardDirty && !getValues("storeCard")) {
            setValue("storeCard", true, { shouldDirty: false, shouldTouch: false });
        }

        previousLoggedInRef.current = isLoggedIn;
    }, [clientPortalConfig.userLoggedIn, formState.dirtyFields?.storeCard, getValues, setValue]);

    const handleSubmit = async (values: PaymentFormType) => {
        await handlePayment(values, amount, true);
    };

    return (
        <div data-test-id="section_stripe_form">
            <Form form={form} formId={formId} onSubmit={handleSubmit} dataTestIdSuffix="stripe_payment">
                {creditCardInfoLoading ? (
                    <LoadingOverlay />
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "8px" }}>
                        {savingPayment && <LoadingOverlay />}
                        <Card
                            sx={{
                                display: "flex",
                                borderRadius: "4px",
                                backgroundColor: alpha(muiPalette.primary.main, 0.1),
                                border: `1px solid ${alpha(muiPalette.primary.main, 0.1)}`,
                            }}
                        >
                            <Typography variant="paragraphExtraLargeRegular">
                                Amount:{" "}
                                <Typography variant="paragraphExtraLargeBold" data-test-id="text_invoice_amount">
                                    {amountToCurrency(totalAmount, clientPortalConfig.currencySymbol)}
                                </Typography>
                            </Typography>
                        </Card>
                        {editCardMode ? (
                            <StripePaymentForm />
                        ) : (
                            <StripeDetailsView creditCardInfo={creditCardInfo} setEditCardMode={setEditCardMode} />
                        )}

                        {clientPortalConfig.userLoggedIn && (
                            <ControlledCheckbox
                                label="Save this card for future charges"
                                name="storeCard"
                                dataTestIdSuffix="store_card"
                            />
                        )}

                        {clientPortalConfig.isStripeFeeEnabled && invoiceIds.length === 1 && (
                            <Typography
                                variant="paragraphSmallRegular"
                                marginLeft="1px"
                                color={palette.neutral["700"]}
                                data-test-id="text_card_processing_fee"
                            >
                                {`Card processing fee of ${amountToCurrency(fee, clientPortalConfig.currencySymbol)} applies`}
                            </Typography>
                        )}

                        <RecaptchaNotice />
                    </Box>
                )}
            </Form>
        </div>
    );
}
